import { useEffect } from "react";

import {
  loadSentry,
  SIGN_IN_FORM_READY_TRANSACTION,
} from "@/monitoring/sentry";

/** Sent by the Investor Portal on every redirect to Auth0 (CX-902, DATA-1). */
export const PORTAL_NAV_START_PARAM = "ext-portal_nav_start";
export const SIGN_IN_ENTRY_PARAM = "ext-entry";

/** Anything slower is a reload, a back navigation or a stale tab, not a load. */
const MAX_MS_FROM_PORTAL_NAV = 60_000;

const SIGN_IN_ENTRIES = ["preboot", "app"] as const;
type SignInEntry = (typeof SIGN_IN_ENTRIES)[number];

export type SignInFormReadyEvent = {
  ms_from_portal_nav: number;
  entry: SignInEntry;
  screen: string;
  ui_locale: string | undefined;
};

type Options = {
  screen: string;
  authorizationParams: Record<string, string> | null | undefined;
  uiLocale: string | undefined;
};

function isSignInEntry(value: string | undefined): value is SignInEntry {
  return SIGN_IN_ENTRIES.includes(value as SignInEntry);
}

/**
 * Builds the `sign_in_form_ready` payload, or `null` when the sample is not a
 * portal-initiated load: no navigation start, unknown entry, or a duration
 * outside 0–60 s.
 */
export function buildSignInFormReadyEvent(
  { screen, authorizationParams, uiLocale }: Options,
  now: number
): SignInFormReadyEvent | null {
  const navStart = Number(authorizationParams?.[PORTAL_NAV_START_PARAM]);
  const entry = authorizationParams?.[SIGN_IN_ENTRY_PARAM];
  if (!Number.isFinite(navStart) || navStart <= 0 || !isSignInEntry(entry)) {
    return null;
  }

  const msFromPortalNav = Math.round(now - navStart);
  if (msFromPortalNav < 0 || msFromPortalNav > MAX_MS_FROM_PORTAL_NAV) {
    return null;
  }

  return {
    ms_from_portal_nav: msFromPortalNav,
    entry,
    screen,
    ui_locale: uiLocale,
  };
}

/** One sample per portal navigation: a UL reload after an error must not count again. */
function claimSample(navStart: string): boolean {
  const key = `sign_in_form_ready:${navStart}`;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}

/**
 * Sends the sample as a Sentry transaction spanning the portal's navigation
 * start to the moment the form was ready, so its duration *is* the metric and
 * the portal's existing Sentry dashboards can chart p50/p75 by `entry`.
 */
async function reportToSentry(
  event: SignInFormReadyEvent,
  navStart: number,
  readyAt: number
): Promise<void> {
  const Sentry = await loadSentry();
  if (!Sentry) return;

  Sentry.withScope((scope) => {
    scope.setTags({
      entry: event.entry,
      screen: event.screen,
      ui_locale: event.ui_locale,
    });
    const span = Sentry.startInactiveSpan({
      name: SIGN_IN_FORM_READY_TRANSACTION,
      op: "ui.load.sign_in_form",
      forceTransaction: true,
      startTime: new Date(navStart),
      attributes: { ...event },
    });
    span.end(new Date(readyAt));
  });
}

/**
 * Reports how long a user waited, from opening the Investor Portal to a usable
 * sign-in form (CX-902). Call it from the screen component: effects run
 * child-first, so the form has mounted when this fires.
 */
export function useSignInFormReady(options: Options): void {
  useEffect(() => {
    const readyAt = Date.now();
    const event = buildSignInFormReadyEvent(options, readyAt);
    const navStart = options.authorizationParams?.[PORTAL_NAV_START_PARAM];
    if (!event || !navStart || !claimSample(navStart)) return;

    void reportToSentry(event, Number(navStart), readyAt);
    // Measured once, on the first render of the screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
